import {
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  Select,
} from "@material-ui/core";
import camelCase from "lodash.camelcase";
import React, {
  MouseEvent,
  ReactNode,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {Controller, UseFormReturn} from "react-hook-form";
import {FormContext} from "view/components/Form";
import clsx from "clsx";
import useStyles from "view/components/Form/styles";
import {
  Cancel as CancelIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlankOutlined,
} from "@material-ui/icons";

export type Option = {
  value: string;
  title: string | React.ReactNode;
};

export type LabeledOption<T extends string = string> = {
  value: T;
  title: string | React.ReactNode;
  label: string;
};

export type MultiSelectOption = {
  value?: string;
  label: string | React.ReactNode;
  subOptions?: MultiSelectOption[];
  disabled?: boolean;
  className?: string;
};

export type MultiSelectOptionParam =
  | MultiSelectOption[]
  | MultiSelectOption
  | null;

type SelectProps = {
  label: string | ReactNode;
  name: string;
  customId?: string;
  options: MultiSelectOption[];
  required?: boolean;
  rules?: Record<string, any>;
  selectStyle?: Record<string, any>;
  defaultValue?: string | string[];
  selectAll?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  showLabel?: boolean;
  selected?: string[];
  displayEmpty?: boolean;
  renderMultipleValue?: (values: any) => ReactNode;
  onChange?: (values: any) => void;
  useRadio?: boolean;
};

const BasicSelect: React.FC<SelectProps> = ({
  label,
  name,
  options,
  customId,
  disabled,
  required = true,
  rules,
  multiple = false,
  selectAll = false,
  selectStyle,
  defaultValue,
  className,
  showLabel = true,
  selected,
  displayEmpty,
  renderMultipleValue: propsRenderMultipleValue,
  onChange: propsOnChange,
  useRadio,
}) => {
  const classes = useStyles();

  const form = useContext(FormContext) as UseFormReturn<Record<string, any>>;
  const id = name || camelCase(label as string);
  const labelId = `${label}-${id}`;

  return (
    <Controller
      control={form.control}
      name={id}
      defaultValue={defaultValue}
      rules={{
        required,
        ...rules,
      }}
      render={({field, fieldState}) => {
        const {onChange, value: controllerValue, name, ref} = field;
        // By default (after first render) controller returns value = undefined
        const allValues = options.map((option) => option.value);
        const isAllSelected = controllerValue?.length === options.length;

        const handleChange = (
          event: React.ChangeEvent<{
            name?: string;
            value: any;
          }>
        ) => {
          const {value} = event.target;
          if (value && value.includes("any")) {
            const newValues = isAllSelected ? [] : allValues;
            onChange(newValues);
            return propsOnChange?.(newValues);
          }
          onChange(value);
          propsOnChange?.(value);
        };

        const handleDelete = (
          valueToDelete: string,
          event: React.MouseEvent
        ) => {
          const newValues = controllerValue.filter(
            (value: string) => value !== valueToDelete
          );
          onChange(newValues);
        };

        const renderMultipleValue = (values: any) => {
          return (
            <MultipleValue
              values={values as string[]}
              options={options}
              handleDelete={handleDelete}
            />
          );
        };

        const renderOption = (option: MultiSelectOption) => {
          const isDisabled =
            (selected?.length ?? 0) > 0 &&
            selected?.includes(option.value || "");

          return (
            <MenuItem
              className={clsx(classes.menuItem, option.className)}
              key={option.value}
              value={option.value}
              disabled={isDisabled || option.disabled}
            >
              {option.label}
            </MenuItem>
          );
        };

        const renderMultipleOption = (option: MultiSelectOption) => {
          const isChecked = controllerValue
            ? controllerValue.indexOf(option.value) > -1
            : defaultValue?.includes(option?.value!); // eslint-disable-line

          const ControlComponent = useRadio ? Radio : Checkbox;

          return (
            <MenuItem
              className={clsx(
                classes.multiselectMenuItem,
                {
                  [classes.multiselectMenuItemSelected]: isChecked,
                },
                option.className
              )}
              key={option.value}
              value={option.value}
            >
              <ControlComponent
                color="primary"
                size="small"
                checked={isChecked}
                {...(!useRadio
                  ? {
                      icon: <CheckBoxOutlineBlankOutlined />,
                      checkedIcon: <CheckBoxIcon />,
                    }
                  : {})}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          );
        };

        const renderSelectAllOption = () => {
          if (multiple && selectAll) {
            const isNotAllSelected =
              controllerValue.length > 0 && !isAllSelected;
            return (
              <MenuItem
                className={clsx(classes.multiselectMenuItem, {
                  [classes.multiselectMenuItemSelected]: isAllSelected,
                })}
                value="any"
              >
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isNotAllSelected}
                  color="primary"
                  size="small"
                />
                <ListItemText
                  primary={isAllSelected ? "Clear All" : "Select All"}
                />
              </MenuItem>
            );
          }
          return null;
        };

        const {invalid} = fieldState;
        return (
          <FormControl
            style={{width: "100%"}}
            variant="outlined"
            error={invalid}
          >
            <InputLabel
              className={classes.inputLabel}
              required={required}
              id={labelId}
            >
              {label}
            </InputLabel>
            <Select
              displayEmpty={displayEmpty}
              id={customId || name}
              // eslint-disable-next-line
              // @ts-ignore
              notched={!showLabel ? false : undefined}
              name={name}
              fullWidth
              classes={{
                select: classes.select,
                icon: classes.selectIcon,
              }}
              className={clsx(classes.input, className)}
              inputRef={ref}
              value={controllerValue ?? []}
              disabled={disabled}
              multiple={multiple}
              renderValue={
                multiple
                  ? propsRenderMultipleValue ?? renderMultipleValue
                  : undefined
              }
              onChange={handleChange}
              labelId={showLabel ? labelId : undefined}
              label={showLabel ? label : null}
              style={selectStyle}
              defaultValue={defaultValue}
              MenuProps={
                multiple
                  ? {
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                      classes: {paper: classes.multiselectMenuList},
                    }
                  : {
                      classes: {paper: classes.menuList},
                    }
              }
            >
              {renderSelectAllOption()}
              {options.map(multiple ? renderMultipleOption : renderOption)}
            </Select>
          </FormControl>
        );
      }}
    />
  );
};

export default BasicSelect;

const MultipleValue = ({
  handleDelete,
  options,
  values,
}: {
  values: string[];
  options: MultiSelectOption[];
  handleDelete: (val: string, event: MouseEvent<HTMLSelectElement>) => void;
}) => {
  const classes = useStyles();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [hiddenChips, setHiddenChips] = useState<Record<string, boolean>>({});

  useLayoutEffect(() => {
    const handle = () => {
      const element = elementRef.current;
      if (!element) {
        return;
      }

      let totalWidth = 0;
      const newHiddenChips: Record<string, boolean> = {};
      const chipElems = Array.from(element.children);
      chipElems.forEach((chip) => {
        const chipEl = chip as HTMLElement;
        const chipWidth = chipEl.offsetWidth;
        totalWidth += chipWidth;

        if (totalWidth > element.clientWidth) {
          newHiddenChips[chipEl.id] = true;
        }
        setHiddenChips(newHiddenChips);
      });
    };

    window.addEventListener("resize", handle);
    handle();

    return () => window.removeEventListener("resize", handle);
  }, [values]);

  return (
    <div className={classes.selectValue} ref={elementRef}>
      {values.map((value: string) => {
        const label = options.find((option) => option.value === value)?.label;
        if (!label) {
          return null;
        }

        return (
          <Chip
            id={value}
            key={value}
            label={label}
            className={classes.multiselectChip}
            style={{pointerEvents: hiddenChips[value] ? "none" : "auto"}}
            onDelete={(event) => handleDelete(value, event)}
            deleteIcon={<CancelIcon />}
            onMouseDown={(event) => event.stopPropagation()}
          />
        );
      })}
    </div>
  );
};
